"use client";
import { useCombobox } from "downshift";
import { useEffect, useRef, useState } from "react";
import { DebouncedState, useDebouncedCallback } from "use-debounce";

import styles from "./AddressSearchBox.module.scss";

import type { AddressSearchSupportContent } from "@/sanity/queries";
import clsx from "clsx";
import { Controller, useFormContext } from "react-hook-form";
import { siteAPIURL } from "../../../utilities/constants";
import { Button, ButtonLink } from "../Button/Button";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";

type AddressSuggestion = {
	text: string;
	globalAddressKey: string;
};

interface AddressSearchBoxProps {
	defaultAddress?: string;
	nmi?: string;
	energy?: string;
	addressSearchSupportContent?: AddressSearchSupportContent | null;
	isNmiSearchModalOpen?: boolean;
	onNmiSearchModalOpenChange?: (isOpen: boolean) => void;
	onClearClicked: () => void;
	onInputChange: (value: string) => void;
	onAddressSelected: (data: any) => void;
}

export const AddressSearchBox = ({
	defaultAddress,
	nmi,
	energy,
	addressSearchSupportContent,
	isNmiSearchModalOpen = false,
	onNmiSearchModalOpenChange,
	onClearClicked,
	onInputChange,
	onAddressSelected,
}: AddressSearchBoxProps) => {
	const nmiDisplayValue = nmi ? getNmiDisplayValue(nmi) : "";
	const isDisplayingNmi = Boolean(nmiDisplayValue && energy !== "gas");
	const [loading, setLoading] = useState(false);
	const [addressForSearch, setAddressForSearch] = useState(
		defaultAddress ? decodeURIComponent(defaultAddress) : "",
	);
	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);

	const abortControllerRef = useRef<AbortController | null>(null);
	const programmaticInputValueRef = useRef<string | null>(null);
	// Set synchronously on click so the blur fired while opening the modal doesn't
	// close the dropdown, keeping the trigger visible for focus restoration on close.
	const nmiModalOpenRef = useRef(false);

	useEffect(() => {
		const abortController = abortControllerRef.current;
		return () => {
			// Cleanup function to abort any pending requests
			if (abortController) {
				abortController.abort();
			}
		};
	}, []);

	// Debounce callback
	const debouncedSearchAddress: DebouncedState<typeof searchAddress> = useDebouncedCallback(
		// function
		(value, onComplete) => {
			searchAddress(value, onComplete);
		},
		// delay in ms
		300,
	);

	const searchAddress = async (
		address: string,
		onComplete?: (suggestions: AddressSuggestion[]) => void,
	) => {
		if (isDisplayingNmi) {
			setAddressSuggestions([]);
			setAddressForSearch(nmiDisplayValue);
			onComplete && onComplete([]);
			return;
		}

		// Abort any pending requests
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		setAddressForSearch(address);

		// Check if the address is long enough
		if (address.length < 3) {
			setAddressSuggestions([]);
			onComplete && onComplete([]);
			return;
		}

		// Create new abort controller
		abortControllerRef.current = new AbortController();
		setLoading(true);

		const url = new URL(siteAPIURL + "/signup/address-search");
		url.searchParams.append("address", address);

		fetch(url, {
			signal: abortControllerRef.current.signal,
		})
			.then(response => response.json())
			.then(data => {
				// handle data
				setLoading(false);

				if (data.suggestions && data.suggestions.length > 0) {
					setAddressSuggestions(data.suggestions);
					onComplete && onComplete(data.suggestions);
				} else {
					setAddressSuggestions([]);
					onComplete && onComplete([]);
				}
			})
			.catch(error => {
				setLoading(false);
				// Handle fetch errors
				if (error.name === "AbortError") {
					// Request was aborted
				} else {
					console.error("Fetch error:", error);
				}
			});
		// const data = await response.json();

		// filter to only include addresses within TAS....

		// return data;
	};

	function clearAddress() {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}

		onClearClicked();
		programmaticInputValueRef.current = "";
		setInputValue("");
		setAddressSuggestions([]);
		setAddressForSearch("");
		setLoading(false);
	}

	async function onSelectAddress(address: AddressSuggestion | null) {
		if (!address) {
			setLoading(false);
			return;
		}

		setLoading(true);
		onAddressSelected({
			globalAddressKey: address.globalAddressKey,
			addressText: address.text,
		});
		setLoading(false);

		// // format the address
		// await fetch(siteAPIURL + `/signup/address-format?globalAddressKey=${address.globalAddressKey}`)
		// 	.then(response => response.json())
		// 	.then(data => {
		// 		data.addressText = address.text;

		// 		onAddressSelected(data);
		// 		setLoading(false);
		// 	});
	}

	const {
		isOpen,
		getMenuProps,
		getItemProps,
		getInputProps,
		getLabelProps,
		setInputValue,
		selectItem,
		closeMenu,
		inputValue,
		highlightedIndex,
	} = useCombobox({
		defaultInputValue: addressForSearch,
		items: addressSuggestions,
		itemToKey: (item: AddressSuggestion | null) => item?.globalAddressKey,
		itemToString: (item: AddressSuggestion | null) => item?.text || "",

		onInputValueChange: ({ inputValue, selectedItem }) => {
			if (programmaticInputValueRef.current === inputValue) {
				programmaticInputValueRef.current = null;
				return;
			}

			if (inputValue !== selectedItem?.text) {
				if (isDisplayingNmi && inputValue === nmiDisplayValue) {
					return;
				}

				onInputChange(inputValue);
				debouncedSearchAddress(inputValue);
			}
		},

		onSelectedItemChange: ({ selectedItem }) => {
			onSelectAddress(selectedItem);
		},
	});

	useEffect(() => {
		const nextInputValue = isDisplayingNmi ? nmiDisplayValue : defaultAddress || "";

		if (inputValue !== nextInputValue) {
			programmaticInputValueRef.current = nextInputValue;
			setInputValue(nextInputValue);
		}

		setAddressForSearch(nextInputValue);
		setAddressSuggestions([]);

		if (isDisplayingNmi && abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
			setLoading(false);
		}
	}, [defaultAddress, inputValue, isDisplayingNmi, nmiDisplayValue, setInputValue]);

	/**
	 * If a default address is provided, search it immediately and select the first result.
	 * Only allow this once
	 */
	const defaultSearchOnce = useRef(true);
	useEffect(() => {
		if (defaultSearchOnce.current) {
			defaultSearchOnce.current = false;
			if (defaultAddress && !isDisplayingNmi) {
				searchAddress(defaultAddress, suggestions => {
					if (suggestions.length > 0) {
						selectItem(suggestions[0]);
					}
				});
			}
		}
	}, [defaultAddress, isDisplayingNmi, selectItem]);

	const isSuggestionsOpen = isOpen && addressForSearch && !isDisplayingNmi;

	const { control } = useFormContext();

	const rootRef = useRef<HTMLDivElement>(null);
	const supportsNmi = energy !== "gas";
	const supportText = supportsNmi
		? (addressSearchSupportContent?.text ?? defaultAddressSearchSupportContent.text)
		: (addressSearchSupportContent?.gasText ?? defaultAddressSearchSupportContent.gasText);
	const contactCta = addressSearchSupportContent?.contactCta;

	useEffect(() => {
		if (!supportsNmi && isNmiSearchModalOpen) {
			nmiModalOpenRef.current = false;
			onNmiSearchModalOpenChange?.(false);
		}
	}, [isNmiSearchModalOpen, onNmiSearchModalOpenChange, supportsNmi]);

	useEffect(() => {
		if (!isNmiSearchModalOpen) {
			nmiModalOpenRef.current = false;
		}
	}, [isNmiSearchModalOpen]);

	// Downshift closes the menu as soon as the input blurs, which would hide the
	// suggestion buttons before keyboard focus can reach them. Keep it open while
	// focus stays within the widget and close it once focus leaves entirely.
	function handleInputBlur(event: React.FocusEvent) {
		if (rootRef.current?.contains(event.relatedTarget as Node | null)) {
			(event as React.FocusEvent & { preventDownshiftDefault?: boolean }).preventDownshiftDefault =
				true;
		}
	}

	function handleRootBlur(event: React.FocusEvent<HTMLDivElement>) {
		if (nmiModalOpenRef.current) {
			return;
		}
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			closeMenu();
		}
	}

	function openNmiSearchModal() {
		nmiModalOpenRef.current = true;
		onNmiSearchModalOpenChange?.(true);
	}

	return (
		<div
			className={styles["address"]}
			ref={rootRef}
			onBlur={handleRootBlur}
		>
			<label
				className={styles["address__label"]}
				{...getLabelProps()}
			>
				What is your connection address?
			</label>
			<div className={styles["address__input-wrapper"]}>
				<div className={styles["address__icon"]}>
					{loading ? (
						<LoadingSpinner
							className={styles["address__loading-spinner"]}
							theme="light"
							size="sm"
						/>
					) : (
						<MaterialIcon name="search" />
					)}
				</div>
				<Controller
					name="address"
					control={control}
					rules={{
						validate: value =>
							value?.trim() ? true : "Please enter your connection address",
					}}
					render={({
						field: { name, onBlur, onChange, ref, value, disabled },
						fieldState: { error },
					}) => (
						<input
							className={clsx(
								styles["address__input"],
								error && styles["address__input--error"],
								isSuggestionsOpen && styles["address__input--suggestions-open"],
							)}
							{...getInputProps({
								placeholder: "Start typing your connection address",
								name: name,
								ref,
								onChange,
								onFocus: () => {
									if (isDisplayingNmi) {
										clearAddress();
									}
								},
								onBlur: handleInputBlur,
							})}
						/>
					)}
				/>

				{inputValue && (
					<button
						className={styles["address__clear"]}
						onClick={clearAddress}
						aria-label="Clear address"
						type="button"
					>
						<MaterialIcon name="close" />
					</button>
				)}
			</div>

			<ul
				{...getMenuProps({
					className: clsx(
						styles["address__suggestion-list"],
						isSuggestionsOpen && styles["address__suggestion-list--open"],
					),
				})}
			>
				{addressSuggestions.map((address, index) => (
					<li
						className={clsx(
							styles["address__suggestion"],
							index === highlightedIndex && styles["address__suggestion--highlighted"],
						)}
						key={address.globalAddressKey}
						{...getItemProps({
							item: address,
						})}
					>
						<MaterialIcon
							className={styles["address__suggestion-icon"]}
							name="location_on"
						/>
						<span>{highlightTextMatch(address.text, inputValue)}</span>
					</li>
				))}
				{addressForSearch && addressSuggestions.length === 0 && (
					<li className={styles["address__suggestion"]}>
						{loading ? "Loading results..." : "No address found"}
					</li>
				)}
				<li
					className={styles["address__support"]}
					role="presentation"
				>
					<div className={styles["address__support-panel"]}>
						<div className={styles["address__support-copy"]}>
							<p className={styles["address__support-title"]}>
								{addressSearchSupportContent?.title ?? defaultAddressSearchSupportContent.title}
							</p>
							<p className={styles["address__support-text"]}>
								{supportText}
							</p>
						</div>
						{(supportsNmi || (contactCta?.link && contactCta.label)) && (
							<div className={styles["address__support-actions"]}>
								{supportsNmi && (
									<Button
										type="button"
										theme="compact"
										label="Search NMI"
										className={styles["address__support-button"]}
										innerClassName={styles["address__support-button-inner"]}
										onClick={openNmiSearchModal}
									/>
								)}
								{contactCta?.link && contactCta.label && (
									<ButtonLink
										href={contactCta.link}
										theme="compact"
										label={contactCta.label}
										target={contactCta.openInNewTab ? "_blank" : undefined}
										className={styles["address__support-button"]}
										innerClassName={styles["address__support-button-inner"]}
									/>
								)}
							</div>
						)}
					</div>
				</li>
			</ul>

		</div>
	);
};

const defaultAddressSearchSupportContent = {
	title: "Can’t find your address?",
	text: "No worries, we can still help get you connected. If you know your National Meter Identifier (NMI), you can search using that. Otherwise, contact us and we’ll arrange a callback to get you connected.",
	gasText:
		"No worries, we can still help get you connected. Contact us and we’ll arrange a callback to get you connected.",
};

function highlightTextMatch(text: string, input: string) {
	const escapedInput = input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	// Create a regular expression that matches the sequence of words in the input
	const regex = new RegExp(escapedInput.split(" ").join("\\s+"), "gi");

	const matches = text.match(regex);
	if (!matches) return <span>{text}</span>;
	return text.split(regex).map((part, index, array) => {
		if (index < array.length - 1) {
			return (
				<span key={index}>
					{part}
					<em>{matches[index]}</em>
				</span>
			);
		}
		return part;
	});
}

const getNmiDisplayValue = (nmi: string) => `NMI: ${nmi}`;
